import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Switch,
  Divider,
  Chip,
  Typography,
  Box,
} from "@mui/material";
import {
  Settings,
  Shield,
  Notifications,
  Palette,
} from "@mui/icons-material";
import Logout from '@mui/icons-material/Logout';
import { useSelector } from "react-redux";


export default function SettingsPage() {
  const [notifications, setNotifications] = useState(true);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Récupérer les informations de l'utilisateur connecté
  const userDetails = JSON.parse(window.localStorage.getItem("user-details") || '{}');
  const user = userDetails?.user || {};
  
  // Initialiser les champs avec les vraies données de l'utilisateur
  React.useEffect(() => {
    if (user.firstname) setFirstName(user.firstname);
    if (user.lastname) setLastName(user.lastname);
    if (user.email) setEmail(user.email);
  }, [user]);

  const getRoleBadge = (role) => {
    if (role === "admin" || role === "administrator") {
      return <Chip label="Administrateur" color="error" />;
    }
    if (role === "manager") {
      return <Chip label="Gestionnaire" color="warning" />;
    }
    if (role === "user" || role === "utilisateur") {
      return <Chip label="Utilisateur" color="default" />;
    }
    return <Chip label={role || "Utilisateur"} color="default" />;
  };

  const handleLogout = () => {
    window.localStorage.removeItem("user-details")
  }

  return (
    <>
      <div className="content-body">
        <div className="container-fluid">
    <Box sx={{ p: 3 }}>
      <Typography variant="h3" gutterBottom>
        Paramètres
      </Typography>

      <Box sx={{ maxWidth: "1000px", display: "flex", flexDirection: "column", gap: 3 }}>
        
        {/* Profil */}
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Settings fontSize="small" />
                <Typography variant="h6">Profil Utilisateur</Typography>
              </Box>
            }
          />
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: 24,
                }}
              >
                {(user.firstname || user.first_name || '').charAt(0).toUpperCase()}
                {(user.lastname || user.last_name || '').charAt(0).toUpperCase()}
              </Box>
              <Box>
                <Typography variant="subtitle1">
                  {user.firstname || user.first_name || 'Prénom'} {user.lastname || user.last_name || 'Nom'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email || 'Email non disponible'}
                </Typography>
                <Box sx={{ mt: 1 }}>{getRoleBadge(user.role || user.user_type)}</Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
               label="Prénom"
               value={firstName} 
               onChange={(e) => setFirstName(e.target.value)}
              />
              <TextField
               label="Nom"
               value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Permissions */}
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Shield fontSize="small" />
                <Typography variant="h6">Permissions et Rôle</Typography>
              </Box>
            }
          />
          <CardContent>
            <Typography variant="subtitle2">Rôle actuel</Typography>
            <Box sx={{ mt: 1, mb: 2 }}>{getRoleBadge(user.role || user.user_type)}</Box>

            <Box sx={{ bgcolor: "grey.100", p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Permissions accordées :
              </Typography>
              <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                {user.role === "admin" || user.user_type === "admin" ? (
                  <>
                    <li>Accès complet à toutes les fonctionnalités</li>
                    <li>Gestion des utilisateurs</li>
                    <li>Configuration du système</li>
                    <li>Accès aux statistiques avancées</li>
                  </>
                ) : user.role === "manager" || user.user_type === "manager" ? (
                  <>
                    <li>Gestion des projets</li>
                    <li>Gestion des demandes</li>
                    <li>Accès aux rapports</li>
                  </>
                ) : (
                  <>
                    <li>Accès aux projets assignés</li>
                    <li>Consultation des demandes</li>
                    <li>Accès restreint (rôle utilisateur)</li>
                  </>
                )}
              </ul>
            </Box>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Notifications fontSize="small" />
                <Typography variant="h6">Notifications</Typography>
              </Box>
            }
          />
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="subtitle2">Activer les notifications</Typography>
                <Typography variant="body2" color="text.secondary">
                  Recevoir des notifications pour les nouvelles commandes.
                </Typography>
              </Box>
              <Switch checked={notifications} onChange={(e) => setNotifications(e.target.checked)} />
            </Box>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="subtitle2">Alertes par email</Typography>
                <Typography variant="body2" color="text.secondary">
                  Recevoir des rapports périodiques par email.
                </Typography>
              </Box>
              <Switch checked={emailAlerts} onChange={(e) => setEmailAlerts(e.target.checked)} />
            </Box>
          </CardContent>
        </Card>

        {/* Apparence */}
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Palette fontSize="small" />
                <Typography variant="h6">Apparence</Typography>
              </Box>
            }
          />
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="subtitle2">Mode sombre</Typography>
                <Typography variant="body2" color="text.secondary">
                  Basculer vers un thème sombre (à venir).
                </Typography>
              </Box>
              <Switch checked={darkMode} onChange={(e) => setDarkMode(e.target.checked)} />
            </Box>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardContent sx={{ display: "flex", gap: 2 }}>
            <Button variant="contained" fullWidth>
              Sauvegarder les Paramètres
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={handleLogout}
              startIcon={<Logout fontSize="small" />}
            >
              Se Déconnecter
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
    </div>
      </div>
    </>
  );
}